import Header from 'components/Headers/Header';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTable, useGlobalFilter } from 'react-table';
import { Card, Table, Container, Row, Button } from 'reactstrap';
import { GlobalFilter } from 'components/Generic/GlobalFilter';
import localforage from 'localforage';
import { convertToFloatBr } from 'utility/generic';
import { syncToSend } from 'config/api';
import ModalDetails from '../modal';

function OfflineAttachmentPreview({ localKey }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    localforage.getItem(localKey).then((stored) => {
      if (stored?.base64) setSrc(stored.base64);
    });
  }, [localKey]);
  if (!src)
    return (
      <span style={{ fontSize: '11px', opacity: 0.7 }}>
        Anexo offline (aguardando sync)
      </span>
    );
  return (
    <img
      src={src}
      alt='anexo'
      style={{
        height: '40px',
        width: '40px',
        objectFit: 'cover',
        borderRadius: '4px',
        border: '1px solid #dee2e6',
        marginTop: '4px',
      }}
    />
  );
}

export function TableGeneric({ columns, data }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
  );

  const { globalFilter } = state;

  return (
    <>
      <div style={{ display: 'flex' }}>
        <GlobalFilter
          filter={globalFilter}
          setFilter={setGlobalFilter}
          disableButtomCreate={true}
        />
        <Button
          color='success'
          onClick={() => {
            syncToSend();
          }}
          size='sm'
          style={{ maxHeight: '48px', marginTop: '22px', marginRight: '20px' }}
        >
          Sincronizar
        </Button>
        {/* <Button
          color='warning'
          onClick={() => console.log('Teste')}
          size='sm'
          style={{ maxHeight: '48px', marginTop: '22px', marginRight: '20px' }}
        >
          Exportar
        </Button> */}
      </div>

      <Table responsive striped hover {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th scope='col' {...column.getHeaderProps()}>
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.length > 0 ? (
            rows.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan='7'
                style={{ textAlign: 'center', width: '20px', fontSize: '14px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '14px',
                    alignItems: 'center',
                  }}
                >
                  Nenhum registro para sincronizar!
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  );
}

export const SyncList = () => {
  const [syncs, setSyncs] = useState([]);

  const handleDelete = useCallback(async (index) => {
    const allRequests = (await localforage.getItem('syncRequests')) || [];
    // O índice na lista filtrada pode não bater com o allRequests, então remove pelo conteúdo
    const item = syncs[index];
    const idx = allRequests.findIndex(
      (r) => r.url === item.url && r.body === item.body,
    );
    if (idx !== -1) {
      // Se tinha anexo offline, limpa do localforage também
      try {
        const parsed = JSON.parse(item.body);
        if (parsed.localAttachmentKey)
          await localforage.removeItem(parsed.localAttachmentKey);
      } catch (_) {}
      allRequests.splice(idx, 1);
      await localforage.setItem('syncRequests', allRequests);
    }
    setSyncs((prev) => prev.filter((_, i) => i !== index));
  }, [syncs]);

  useEffect(() => {
    async function getData() {
      const syncRequests = (await localforage.getItem('syncRequests')) || [];
      setSyncs(
        syncRequests.filter(
          (error) =>
            error.url === '/v1/sale' ||
            error.url === '/v1/sale/execute' ||
            error.url === 'whatsapp/v1/messages',
        ),
      );
    }

    getData();
  }, []);

  const cacheItemsDisabled = React.useMemo(() => syncs, [syncs]);

  const formatProductDetails = (product, client, isSale) => {
    const value = convertToFloatBr(product.value);
    const commission = parseFloat(client.commission) / 100;

    const calculateReceivable = (quantity) =>
      value * parseFloat(quantity) * (1 - commission);

    let details = `Produto: ${product.name}`;

    if (isSale) {
      details += ` | Quantidade: ${
        product.quantity
      } | À Receber: ${calculateReceivable(product.quantity)}`;

      return details;
    }

    const totalReceivable = calculateReceivable(product.sold);
    const totalPaid = convertToFloatBr(product.amount_paid);
    const debtor =
      totalPaid === 0 ? totalReceivable : Math.abs(totalPaid - totalReceivable);

    details +=
      ` | Pre-Venda: ${product.quantity} | Venda: ${product.sold}` +
      ` | A Receber: ${totalReceivable.toFixed(2)} | Pago: ${totalPaid}` +
      ` | Devedor: ${debtor.toFixed(2)} | Devolucao: ${product.returned}`;

    return details;
  };

  const isWhatsapp = (url) => url === 'whatsapp/v1/messages';

  const columns = useMemo(
    () => [
      {
        Header: 'Operação',
        Cell: ({ ...props }) => {
          const operation = props.data[props.row.index]?.url;
          const label = isWhatsapp(operation)
            ? 'WhatsApp'
            : operation === '/v1/sale'
              ? 'Pré-Venda'
              : 'Venda';
          const color = isWhatsapp(operation) ? '#25D366' : undefined;
          return (
            <span style={{ color, fontWeight: color ? 'bold' : undefined }}>
              {label}
            </span>
          );
        },
      },
      {
        Header: 'Cliente',
        Cell: ({ ...props }) => {
          const { url, body } = props.data[props.row.index];
          const parsed = JSON.parse(body);
          if (isWhatsapp(url)) {
            return <span>{parsed.clientName || `ID ${parsed.clientId}`}</span>;
          }
          return <span>{parsed.client?.name}</span>;
        },
      },
      {
        Header: 'Cidade',
        Cell: ({ ...props }) => {
          const { url, body } = props.data[props.row.index];
          const parsed = JSON.parse(body);
          if (isWhatsapp(url)) return <span>—</span>;
          return <span>{parsed.client?.city}</span>;
        },
      },
      {
        Header: 'Comissão',
        Cell: ({ ...props }) => {
          const { url, body } = props.data[props.row.index];
          const parsed = JSON.parse(body);
          if (isWhatsapp(url)) return <span>—</span>;
          return <span>{parsed.client?.commission}</span>;
        },
      },
      {
        Header: 'Detalhes',
        Cell: ({ ...props }) => {
          const { url, body } = props.data[props.row.index];
          const parsed = JSON.parse(body);
          if (isWhatsapp(url)) {
            return (
              <div>
                <span>{parsed.messageBody}</span>
                {parsed.localAttachmentKey && (
                  <OfflineAttachmentPreview
                    localKey={parsed.localAttachmentKey}
                  />
                )}
                {parsed.attachmentUrl && !parsed.localAttachmentKey && (
                  <>
                    <br />
                    <span style={{ fontSize: '11px', opacity: 0.7 }}>
                      Anexo: {parsed.attachmentName || parsed.attachmentUrl}
                    </span>
                  </>
                )}
                {parsed.scheduledAt && (
                  <>
                    <br />
                    <span style={{ fontSize: '11px', opacity: 0.7 }}>
                      Agendado:{' '}
                      {new Date(parsed.scheduledAt).toLocaleString('pt-BR')}
                    </span>
                  </>
                )}
              </div>
            );
          }
          return (
            <div>
              {parsed.productList?.map((product, index) => (
                <React.Fragment key={index}>
                  <span>
                    {formatProductDetails(
                      product,
                      parsed.client,
                      url === '/v1/sale',
                    )}
                  </span>
                  <br />
                </React.Fragment>
              ))}
            </div>
          );
        },
      },
      {
        Header: 'Ação',
        Cell: ({ ...props }) => {
          const index = props.row.index;
          const { url, body } = props.data[index];
          return (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {!isWhatsapp(url) && (
                <ModalDetails
                  buttonLabel={'Imprimir'}
                  titleModal={'Pré-Venda'}
                  content={body}
                  size={'sm'}
                />
              )}
              <Button
                color='danger'
                size='sm'
                onClick={() => handleDelete(index)}
              >
                Excluir
              </Button>
            </div>
          );
        },
      },
    ],
    [handleDelete],
  );

  return (
    <>
      <Header />
      <Container className='mt--7' fluid>
        <Row>
          <div className='col'>
            <Card className='shadow'>
              <TableGeneric columns={columns} data={cacheItemsDisabled} />
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};
